import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter, Link, NavLink, Route, Routes, useNavigate,
} from "react-router-dom";
import { get, post } from "./api";
import { LoginPage } from "./pages/Login";
import { WorkspacesPage } from "./pages/Workspaces";
import { WorkspaceDetailPage } from "./pages/WorkspaceDetail";
import { SiteDetailPage } from "./pages/SiteDetail";
import { AccountSettingsPage } from "./pages/AccountSettings";
import { I18nProvider, LanguageSwitcher, useI18n } from "./i18n";

interface Me {
  email: string;
  email_verified: boolean;
  workspaces: { id: string; name: string; capabilities: string[] }[];
}

function Header({ me, onLogout }: { me: Me | null; onLogout: () => void }) {
  const { t } = useI18n();

  return (
    <header className="topbar">
      <Link to="/" className="brand">{t.common.brand}</Link>
      {me && (
        <>
          <nav>
            <NavLink to="/">{t.common.workspaces}</NavLink>
            <NavLink to="/account">{t.common.accountSecurity}</NavLink>
          </nav>
          <div className="spacer" />
          <span className="muted">{me.email}</span>
          <LanguageSwitcher />
          <button className="ghost" onClick={onLogout}>{t.common.logout}</button>
        </>
      )}
      {!me && (
        <>
          <div className="spacer" />
          <LanguageSwitcher />
        </>
      )}
    </header>
  );
}

function Shell() {
  const { t } = useI18n();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [reload, setReload] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setMe(undefined);
    get<Me>("/api/v1/auth/me")
      .then(setMe)
      .catch(() => setMe(null));
  }, [reload]);

  if (me === undefined) return <div className="center muted">{t.common.loading}</div>;

  // email-link entries (/auth/verify, /auth/reset, /invite) must work
  // WITHOUT a session (F16: links used to fall back to the login page)
  if (me === null) {
    return (
      <>
        <Header me={null} onLogout={() => {}} />
        <main className="content">
          <Routes>
            <Route path="/auth/verify" element={<VerifyHandler />} />
            <Route path="/auth/reset" element={<ResetHandler />} />
            <Route path="/invite" element={<InviteHandler onDone={() => setReload((n) => n + 1)} />} />
            <Route path="*" element={<LoginPage onDone={() => setReload((n) => n + 1)} />} />
          </Routes>
        </main>
      </>
    );
  }

  return (
    <>
      <Header
        me={me}
        onLogout={async () => {
          await post("/api/v1/auth/logout");
          setReload((n) => n + 1);
          navigate("/");
        }}
      />
      <main className="content">
        <Routes>
          <Route path="/" element={<WorkspacesPage me={me} />} />
          <Route path="/account" element={<AccountSettingsPage onUpdated={() => setReload((n) => n + 1)} />} />
          <Route path="/w/:workspaceId" element={<WorkspaceDetailPage />} />
          <Route path="/w/:workspaceId/sites/:siteId" element={<SiteDetailPage />} />
          <Route path="/auth/verify" element={<VerifyHandler />} />
          <Route path="/auth/reset" element={<ResetHandler />} />
          <Route path="/invite" element={<InviteHandler onDone={() => setReload((n) => n + 1)} />} />
        </Routes>
      </main>
    </>
  );
}

function VerifyHandler() {
  const { t } = useI18n();
  const [msg, setMsg] = useState<string>(t.auth.verifyingEmail);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    post<{ workspace_id: string | null }>("/api/v1/auth/verify-email", { token })
      .then((r) => setMsg(r.workspace_id ? t.auth.verifySuccess : t.auth.alreadyVerified))
      .catch((e) => setMsg(`${t.auth.verifyFailed}${e.message}`));
  }, [t]);
  return <div className="center card">{msg} <Link to="/">{t.common.back}</Link></div>;
}

function ResetHandler() {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  async function submit() {
    if (password !== confirm) {
      setErr(t.auth.passwordMismatch);
      return;
    }
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/api/v1/auth/reset-password/confirm", { token, password });
      setMsg(t.auth.resetSuccess);
      setPassword(""); setConfirm("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 380, margin: "0 auto" }}>
        <h2>{t.auth.resetPasswordHeading}</h2>
        {!token && <p className="error">{t.auth.missingResetToken}</p>}
        <label>{t.auth.newPasswordLabel}</label>
        <input type="password" value={password}
               onChange={(e) => setPassword(e.target.value)} />
        <label>{t.auth.confirmPasswordLabel}</label>
        <input type="password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} />
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button className="primary" disabled={busy || !token} onClick={submit}>
            {busy ? t.common.submitting : t.auth.resetPasswordHeading}
          </button>
        </div>
        {msg && <p className="muted">{msg}</p>}
        {err && <p className="error">{err}</p>}
        <p><Link to="/">{t.auth.backToLogin}</Link></p>
      </div>
    </div>
  );
}

function InviteHandler({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [state, setState] = useState<"busy" | "need-login" | "ok" | "error">("busy");
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string>(t.auth.acceptingInvite);
  const started = useRef(false);
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";
  const workspace = params.get("workspace") ?? "";

  useEffect(() => {
    if (started.current) return;   // React StrictMode double-mount guard
    started.current = true;
    if (!token || !workspace) {
      setState("error");
      setErr(t.auth.invalidInviteLink);
      return;
    }
    get<Me>("/api/v1/auth/me")
      .then(async (me) => {
        try {
          await post(`/api/v1/workspaces/${workspace}/members/accept`, { token });
          setState("ok");
          setMsg(`${t.auth.joinedWorkspace}${me.email}`);
          onDone();
        } catch (e) {
          try {
            const me2 = await get<Me>("/api/v1/auth/me");
            if (me2.workspaces.some(w => w.id === workspace)) {
              setState("ok");
              setMsg(`${t.auth.joinedWorkspace}${me2.email}`);
              return;
            }
          } catch { /* fall through to original error */ }
          setState("error");
          setErr((e as Error).message);
        }
      })
      .catch(() => {
        setState("need-login");
        setMsg(t.auth.needLoginToAccept);
      });
  }, [token, workspace, onDone, t]);

  return (
    <div className="center card" style={{ maxWidth: 420 }}>
      <h2>{t.auth.inviteHeading}</h2>
      {state === "need-login"
        ? <p className="muted">{msg}</p>
        : state === "ok"
          ? <p className="muted">{msg}</p>
          : state === "busy"
            ? <p className="muted">{msg}</p>
            : <p className="error">{err}</p>}
      <p><Link to="/">{t.common.back}</Link></p>
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </I18nProvider>
  );
}
